import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { HiOutlineBuildingLibrary, HiOutlineCalendar, HiOutlinePhoto, HiOutlineMagnifyingGlass } from 'react-icons/hi2';
import { useAdmin } from '../context/AdminContext';
import { PositionControls } from './PositionControls';

export function ExhibitionList({ exhibitions = [], paintings = [], onEdit, onDelete, entityViews = {} }) {
  const { t, i18n } = useTranslation();
  const { handleExhibitionPositionUpdate, loading } = useAdmin();
  const [search, setSearch] = useState('');
  const [filteredExhibitions, setFilteredExhibitions] = useState([]);
  // const [sortMode, setSortMode] = useState(false);
  const [positionInputs, setPositionInputs] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);

  // Filter exhibitions based on search
  useEffect(() => {
    if (!Array.isArray(exhibitions)) {
      setFilteredExhibitions([]);
      return;
    }

    if (!search.trim()) {
      // Sort by start_date (newest first)
      const sorted = [...exhibitions].sort((a, b) => {
        const dateA = a.start_date ? new Date(a.start_date) : new Date(0);
        const dateB = b.start_date ? new Date(b.start_date) : new Date(0);
        return dateB - dateA; // Newest first
      });
      setFilteredExhibitions(sorted);
    } else {
      const searchLower = search.toLowerCase();
      const filtered = exhibitions.filter(e => 
        (e.name && e.name.toLowerCase().includes(searchLower)) ||
        (e.locations && e.locations.toLowerCase().includes(searchLower))
      );
      // Sort by start_date (newest first)
      const sorted = filtered.sort((a, b) => {
        const dateA = a.start_date ? new Date(a.start_date) : new Date(0);
        const dateB = b.start_date ? new Date(b.start_date) : new Date(0);
        return dateB - dateA; // Newest first
      });
      setFilteredExhibitions(sorted);
    }
  }, [exhibitions, search]);

  // Position Update Handler
  const handlePositionUpdateWrapper = async (exhibitionId, newPosition) => {
    if (isUpdating) return;
    
    setIsUpdating(true);
    
    try {
      await handleExhibitionPositionUpdate(exhibitionId, newPosition);
    } catch (error) {
      alert(t('exhibitionlist.position_error', 'Fehler beim Aktualisieren der Position'));
    } finally {
      setIsUpdating(false);
    }
  };

  // Helper function to count paintings for an exhibition
  const getPaintingCount = (exhibitionId) => {
    return paintings.filter(p => Array.isArray(p.exhibition_ids) && p.exhibition_ids.includes(exhibitionId)).length;
  };

  // Helper to get name/locations in current language
  const getName = (e) => {
    return e.name || '';
  };
  
  const getLocations = (e) => {
    return e.locations || '';
  };

  // Helper to format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    return d.toLocaleDateString(i18n.language === 'de' ? 'de-DE' : 'en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  return (
    <div>
      <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">{t('exhibitionform.current_exhibitions')}</h3>
      
      {/* Header mit Suchleiste und Sortier-Toggle */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        {/* Search Bar */}
        <div className="flex-1 flex items-center gap-2 bg-white dark:bg-zinc-800 rounded-lg shadow px-3 py-2 border border-gray-200 dark:border-zinc-700">
          <HiOutlineMagnifyingGlass className="w-5 h-5 text-gray-400 dark:text-gray-500" />
          <input
            type="text"
            className="flex-1 bg-transparent outline-none text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 text-sm"
            placeholder={t('exhibitionform.search_placeholder', 'Suche in Ausstellungen...')}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        {/* Sortier-Modus Toggle - auskommentiert da automatische Sortierung aktiv */}
      </div>

      {loading ? (
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-900 dark:border-gray-100 mx-auto" />
      ) : (!filteredExhibitions || filteredExhibitions.length === 0) ? (
        <div className="text-gray-500 text-center">{search ? t('exhibitionform.no_search_results', 'Keine Ausstellungen gefunden') : t('exhibitionform.no_exhibitions', 'Keine Ausstellungen vorhanden')}</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4">
          {filteredExhibitions.map(e => {
            const paintingCount = getPaintingCount(e.id);
            return (
              <div key={e.id} className="flex flex-col h-full">
                {/* Main Card */}
                <div className="bg-white dark:bg-zinc-800 rounded-lg shadow hover:shadow-lg transition-shadow duration-200 border border-gray-200 dark:border-zinc-700 hover:border-blue-200 dark:hover:border-blue-700 flex flex-col h-full relative">
                  {/* Position Badge (only in sort mode) - auskommentiert da automatische Sortierung aktiv */}
                  
                  {/* Content Section */}
                  <div className="p-2 md:p-3 flex flex-col gap-2 flex-grow">
                    {/* Title */}
                    <div className="flex gap-2">
                      <HiOutlineBuildingLibrary className="text-blue-500 flex-shrink-0 mt-0.5" />
                      <div className="min-h-[2.5rem]">
                        <h3 className="font-semibold text-sm md:text-base text-gray-900 dark:text-white leading-tight" title={getName(e)}>
                          {getName(e)}
                        </h3>
                      </div>
                    </div>
                    
                    {/* Date and Location */}
                    <div className="flex flex-col gap-1 text-xs md:text-sm text-gray-600 dark:text-gray-400">
                      <div className="flex items-center gap-1">
                        <HiOutlineCalendar className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">
                          {formatDate(e.start_date)}{e.end_date ? ` – ${formatDate(e.end_date)}` : ''}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <HiOutlineBuildingLibrary className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{getLocations(e)}</span>
                      </div>
                    </div>

                    {/* Meta Info */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
                        <HiOutlinePhoto className="w-3 h-3" />
                        <span>{paintingCount}</span>
                      </div>
                      {typeof entityViews[e.id] === 'number' && (
                        <div className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-3 h-3"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 616 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                          {entityViews[e.id]}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-1 md:gap-2 mt-auto pt-1">
                      <button
                        type="button"
                        onClick={() => onEdit && onEdit(e)}
                        className="flex-1 px-2 md:px-3 py-1 md:py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs md:text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => onDelete && onDelete(e.id)}
                        className="flex-1 px-2 md:px-3 py-1 md:py-1.5 bg-red-600 hover:bg-red-700 text-white rounded text-xs md:text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-red-500"
                      >
                        Del
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Position Controls */}
                {/* <PositionControls
                  item={e}
                  totalItems={exhibitions.length}
                  positionInputs={positionInputs}
                  setPositionInputs={setPositionInputs}
                  onPositionUpdate={handlePositionUpdateWrapper}
                  isUpdating={isUpdating}
                  sortMode={sortMode}
                /> */}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}